import { AxiosError } from 'axios';
import { Stack } from '@fluentui/react';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import React, { useReducer, useRef } from 'react';
import { Redirect, Route, RouteComponentProps, useLocation, withRouter } from 'react-router-dom';
import { AuthProvider, getConfigsForUser, QueueConfiguration, trackException } from '../services';
import { Settings } from './admin';
import { Footer, Unauthorized } from './layout';
import { Review } from './review';
import { NavBar } from './navigation';
import { formatEmailHref } from './util';
import { Lookup } from './lookup';
import { Dashboard } from './dashboard';
import { ErrorBoundary } from './error/ErrorBoundary';
import { useEffect } from 'react';
import { useState } from 'react';
import { AdminState, adminReducer, AdminContext } from '../Store/Admin';
import { CreateQueueFlow } from './createQueueFlow/CreateQueueFlow';
import { TetrisPage } from './tetrispage';
import { RulesDocComponent } from './rulesdoc/RulesDocComponent';

const adminInitSeed: AdminState = {
  queueDataState: undefined,
  userInput: '',
  saveActive: false,
  isUpdated: false,
  updateModerator: {
    userId: undefined,
    isAdd: false,
    isRemove: false,
    isUpdate: false
  },
  updateAdmin: {
    userId: undefined,
    isAdd: false,
    isRemove: false,
    isUpdate: false
  },
  currentTenantDisplayName: '',
  adminSettingUpdateCount: 0
};

export function useQuery(): URLSearchParams {
  const search = useLocation().search;
  return new URLSearchParams(search);
}

const App: React.FC<RouteComponentProps> = props => {
  const [isUnauthorized, setIsUnauthorized] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [queueData, setQueueData] = useState<QueueConfiguration[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const locationPath = useLocation().pathname;
  const [showFooter, setShowFooter] = useState(true);
  const [state, dispatch] = useReducer(adminReducer, adminInitSeed);
  const userId = AuthProvider.getAccount().userName;
  const setMainContainer = useRef<HTMLElement>(null);
  useEffect(() => {
    getConfigsForUser()
      .then(data => {
        setIsUnauthorized(false);
        setIsLoading(false);
        if (data !== null && data.length > 0) {
          setQueueData(data);
        } else {
          // No Queue Data
          trackException(new Error(`App: getConfigsForUser ${userId} returned no queues.`));
        }
      })
      .catch((axiosError: AxiosError) => {
        setIsLoading(false);
        setIsError(true);
        setErrorMessage(axiosError.message);
        axiosError.message = 'App: getConfigsForUser Error: ' + axiosError.message;
        trackException(axiosError);
        if (axiosError && axiosError.isAxiosError && axiosError.response && axiosError.response.status === 401) {
          setIsUnauthorized(true);
        } else {
          setIsUnauthorized(false);
        }
      });
  }, []);

  // TODO: Source of truth for user's queues to be in the data store, and not in a state variable (queueData)
  useEffect(() => {
    const updatedQueue = state.queueDataState;
    if (updatedQueue) {
      const updatedQueueData = [...queueData];
      updatedQueueData[
        updatedQueueData.findIndex(
          queue =>
            queue.tenant.tenantId === updatedQueue.tenant.tenantId &&
            queue.title.titleId === updatedQueue.title.titleId &&
            queue.subjectType === updatedQueue.subjectType
        )
      ] = updatedQueue;
      setQueueData(updatedQueueData);
    }
  }, [state.adminSettingUpdateCount]);

  useEffect(() => {
    setShowFooter(
      locationPath.includes('dashboard') ||
        locationPath.includes('settings') ||
        locationPath.includes('create-queue-flow')
    );
  }, [locationPath]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (event.key === 'Enter' && target.getAttribute('href') === '#main') {
        event.preventDefault();
        if (target.tagName === 'A' && target.getAttribute('href') === '#main') {
          const nextInteractiveElement = setMainContainer.current?.querySelector<HTMLElement>('button, [href]');
          if (nextInteractiveElement) {
            nextInteractiveElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  // Error state for loading config.
  const createGmcErrorMessage = (): JSX.Element => {
    const emailBodyHref = formatEmailHref(
      'Queue',
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      errorMessage
    );
    return (
      <MessageBar messageBarType={MessageBarType.error}>
        Unable to fetch queues. Refresh page and if the issue persists,<a href={emailBodyHref}>submit feedback</a>.
      </MessageBar>
    );
  };

  if (isLoading) {
    return <Spinner size={SpinnerSize.large} className="fixed-top w-100 h-100" />;
  }

  if (isUnauthorized) {
    return <Unauthorized />;
  }

  if (isError) {
    return createGmcErrorMessage();
  }
  const skipToMainContent = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const nextInteractiveElement = setMainContainer.current?.querySelector<HTMLElement>('button, [href]');
    if (nextInteractiveElement) {
      nextInteractiveElement.focus();
    }
  };
  return (
    <AdminContext.Provider value={{ state, dispatch }}>
      <Stack className="h-100">
        <div className="skip-link">
          <a href="#main" onClick={skipToMainContent}>
            {'Skip to Main Content'}
          </a>
        </div>
        <Stack.Item>
          <NavBar {...props} queues={queueData} />
        </Stack.Item>

        <ErrorBoundary>
          <Stack.Item grow className="overflow-hidden h-100">
            <main id="main-content" ref={setMainContainer} className="h-100" style={{ position: 'relative' }}>
              <Route exact path="/" render={(): JSX.Element => <Redirect to="/dashboard" />} />
              <Route exact path="/dashboard" render={(): JSX.Element => <Dashboard configs={queueData} />} />
              <Route exact path="/review" render={(props): JSX.Element => <Review {...props} />} />
              <Route
                path={`/settings/tenantId/:tenantId/titleId/:titleId/subjectType/:subjectType`}
                render={(props): JSX.Element => <Settings {...props} configs={queueData} />}
              />
              {/* Disabling rulesdoc flow outside of dev, until it is ready for prod */}
              {process.env.NODE_ENV === 'development' && (
                <Route
                  path={`/rulesdoc/tenantId/:tenantId`}
                  render={(): JSX.Element => <RulesDocComponent configs={queueData} />}
                />
              )}
              <Route
                path={`/create-queue-flow/tenantId/:tenantId`}
                render={(props): JSX.Element => <CreateQueueFlow {...props} configs={queueData} />}
              />
              <Route exact path="/lookup" render={(props): JSX.Element => <Lookup {...props} configs={queueData} />} />
              {process.env.NODE_ENV === 'development' && (
                <Route exact path="/tetris" render={(): JSX.Element => <TetrisPage />} />
              )}
            </main>
            {showFooter && (
              <Stack.Item>
                <Footer />
              </Stack.Item>
            )}
          </Stack.Item>
        </ErrorBoundary>
      </Stack>
    </AdminContext.Provider>
  );
};

export default withRouter(App);
